import type { FC } from 'react';
import type { A2uiNodeViewProps } from '@/app/lib/a2ui/catalog.tsx';
import { str } from '@/app/lib/a2ui/props.ts';
import { Calculator, Ruler } from 'lucide-react';

export const LakdiEstimate: FC<A2uiNodeViewProps> = ({ node }) => {
  const item = str(node.props.item);
  const size = str(node.props.size);
  const style = str(node.props.style);
  const material = str(node.props.material);
  const calculation = str(node.props.calculation);
  const baseAmount = str(node.props.baseAmount);
  const lowEstimate = str(node.props.lowEstimate);
  const highEstimate = str(node.props.highEstimate);
  const qualification = str(node.props.qualification);
  const softCloseNote = str(node.props.softCloseNote);

  return (
    <main className="@container/lakdi-estimate mx-auto w-full max-w-container-2xl min-w-0">
      <article className="overflow-hidden rounded-3xl border border-border bg-card text-card-foreground shadow-elevated">
        <section className="bg-gradient-to-br from-card via-muted to-primary/10 p-5 @md/lakdi-estimate:p-8">
          <div className="flex flex-col gap-4 @lg/lakdi-estimate:flex-row @lg/lakdi-estimate:items-start @lg/lakdi-estimate:justify-between">
            <div className="flex min-w-0 items-start gap-3">
              <span className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-primary/20 bg-card text-primary">
                <Calculator className="h-5 w-5" aria-hidden="true" />
              </span>
              <div className="min-w-0 space-y-2">
                <p className="text-base font-semibold text-primary">Rough estimate</p>
                <h2 className="break-words font-display text-balance text-2xl font-semibold leading-tight tracking-tight text-foreground @md/lakdi-estimate:text-3xl">
                  {item}
                </h2>
              </div>
            </div>
            <div className="min-w-0 rounded-2xl border border-primary/15 bg-card/80 p-4 @lg/lakdi-estimate:min-w-[16rem]">
              <p className="text-base text-muted-foreground">Estimated range</p>
              <p className="mt-1 break-words font-display text-3xl font-semibold leading-tight tabular-nums text-foreground @md/lakdi-estimate:text-4xl">
                {lowEstimate} – {highEstimate}
              </p>
            </div>
          </div>
          {qualification ? (
            <p className="mt-5 max-w-3xl text-base leading-relaxed text-foreground">{qualification}</p>
          ) : null}
        </section>

        <section className="space-y-5 p-5 @md/lakdi-estimate:p-8">
          <dl className="grid grid-cols-1 gap-x-8 gap-y-4 @md/lakdi-estimate:grid-cols-2">
            <div className="min-w-0 space-y-1">
              <dt className="text-base text-muted-foreground">Size / unit</dt>
              <dd className="break-words text-base font-medium text-foreground">{size}</dd>
            </div>
            <div className="min-w-0 space-y-1">
              <dt className="text-base text-muted-foreground">Style</dt>
              <dd className="break-words text-base font-medium text-foreground">{style}</dd>
            </div>
            <div className="min-w-0 space-y-1">
              <dt className="text-base text-muted-foreground">Material tier</dt>
              <dd className="break-words text-base font-medium text-foreground">{material}</dd>
            </div>
            <div className="min-w-0 space-y-1">
              <dt className="text-base text-muted-foreground">Rate-card base</dt>
              <dd className="break-words text-base font-medium tabular-nums text-foreground">{baseAmount}</dd>
            </div>
          </dl>

          <div className="flex min-w-0 items-start gap-3 rounded-2xl border border-border bg-muted/60 p-4">
            <Ruler className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
            <div className="min-w-0 space-y-1">
              <p className="text-base font-semibold text-foreground">Calculation</p>
              <p className="break-words text-base leading-relaxed text-foreground">{calculation}</p>
            </div>
          </div>

          {softCloseNote ? (
            <p className="border-s-2 border-primary/40 ps-4 text-base leading-relaxed text-muted-foreground">
              {softCloseNote}
            </p>
          ) : null}
        </section>
      </article>
    </main>
  );
};
